<template>
    <v-form-tile>
        <h2 slot="title">User Permissions</h2>
        <p slot="description" class="text-muted">Rebilly offers granular access controls.  Copy permissions from another user, use preset defaults, or customize for complete control over permissions. <a target="_blank" tabindex="-1" href="https://help.rebilly.com/rebilly-basics/users-and-roles">Click here to learn more...</a></p>
        <div slot="content" class="tile">
            <div class="tile-content">
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <v-radio
                            group="accessType"
                            label="Full Access (Administrator)"
                            :value="false"
                            class="stack-m"
                            :model="isLimitedAccess"
                            @update="updateLimitedAccess"
                        />
                        <v-radio
                            group="accessType"
                            label="Limited Access"
                            :value="true"
                            :model="isLimitedAccess"
                            @update="updateLimitedAccess"
                        />
                    </div>
                </div>
            </div>
            <div class="tile-content" v-if="isLimitedAccess">
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <p class="stack-s">Permission Type</p>
                        <v-radio
                            v-if="hasOtherUsersTemplates"
                            group="templateType"
                            label="Copy From another User"
                            class="stack-m"
                            :value="permissionSources.otherUsers"
                            :model="currentTemplateGroup"
                            @update="updateTemplateGroup"
                        />
                        <v-radio
                            group="templateType"
                            label="Copy From a Role Template"
                            class="stack-m"
                            :value="permissionSources.roles"
                            :model="currentTemplateGroup"
                            @update="updateTemplateGroup"
                        />
                        <v-radio
                            group="templateType"
                            label="Customized for this User"
                            class="stack-m"
                            :value="permissionSources.customized"
                            :model="currentTemplateGroup"
                            @update="updateTemplateGroup"
                        />
                    </div>
                    <div v-if="isOtherUsersTemplate" class="grid-item grid-item-half">
                        <v-select
                            :options="templateGroups.otherUsers.templates"
                            :value="templateGroups.otherUsers.selected"
                            @option-select="templateSelected($event, templateGroups.otherUsers)"
                            label="Copy Permissions from Another Limited User"
                        />
                    </div>
                    <div v-else-if="isRoleTemplate" class="grid-item grid-item-half">
                        <v-select
                            :options="templateGroups.roles.templates"
                            :value="templateGroups.roles.selected"
                            @option-select="templateSelected($event, templateGroups.roles)"
                            label="Copy Permissions from a Role Template"
                        />
                    </div>
                    <div v-else class="grid-item grid-item-full">
                        <p class="text-muted">
                            Customized restrictions allow you to give full access to particular resources or to limit them to specific access types (Browse/Read, Edit, Add and Delete).
                        </p>
                    </div>
                    <template v-if="isPermissionGridVisible">
                        <div class="grid-item grid-item-full align-right">
                            <r-button @click="clearAllSelected">Clear All Selected</r-button>
                            <div class="divider"></div>
                        </div>
                        <div class="grid-item grid-item-full">
                            <ul class="list list-group list-divided">
                                <li v-for="{resourceName, resourceLabel, actions} in allPermissions"
                                    :key="`permission-item-${resourceName}`"
                                    class="list-item">
                                    <span class="list-item-content">{{resourceLabel}}</span>
                                    <div class="no-flex">
                                        <r-checkbox v-for="{label, action} in labelActionMap"
                                                    :key="`${resourceName}-${action}`"
                                                    :value="action"
                                                    class="inline-s"
                                                    style="display: inline-block"
                                                    :selected="actions"
                                                    @input="updateAction(resourceName, $event)">
                                            <span slot="label" class="text-muted">{{label}}</span>
                                        </r-checkbox>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </v-form-tile>
</template>

<script>
    import {
        vFormTile, vRadio, vSelect,
    } from '@/components/form';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import {isAdmin, methodToActionPermissions} from '@/common/auth/auth-helper';
    import RESOURCES from '@/common/auth/definitions/resources';
    import {ACTIONS} from '@/common/auth/definitions/actions-methods';
    import _ from '@/common/helpers';

    /* eslint-disable import/no-duplicates */
    import roleTemplates from './role-templates';
    import {getMatchingRole} from './role-templates';
    /* eslint-enable */

    const labelActionMap = [
        {label: 'Full Access', action: ACTIONS.all},
        {label: 'Browse/Read', action: ACTIONS.read},
        {label: 'Edit', action: ACTIONS.edit},
        {label: 'Add', action: ACTIONS.add},
        {label: 'Delete', action: ACTIONS.delete},
    ];

    const permissionSources = {
        otherUsers: 'otherUsers',
        roles: 'roles',
        customized: 'customized',
    };

    export default {
        name: 'permission-form',
        mixins: [LoadStateMixin],
        components: {
            vSelect,
            vFormTile,
            vRadio,
        },
        props: {
            value: Object,
            userId: String,
        },
        data() {
            return {
                isLimitedAccess: !_.isEmpty(this.value),
                templateGroups: {
                    [permissionSources.customized]: {...(this.value || {})},
                    [permissionSources.otherUsers]: {
                        selected: null,
                        templates: [],
                    },
                    [permissionSources.roles]: {
                        selected: this.getSelectedRole(),
                        templates: roleTemplates,
                    },
                },
                currentTemplateGroup: this.getDefaultTemplateGroup(),
                resources: RESOURCES,
                permissionSources,
                labelActionMap,
            };
        },
        computed: {
            otherUsersGroup() {
                return this.templateGroups[permissionSources.otherUsers];
            },
            rolesGroup() {
                return this.templateGroups[permissionSources.roles];
            },
            customizedPermissions() {
                return this.templateGroups[permissionSources.customized];
            },
            currentGroup() {
                return this.templateGroups[this.currentTemplateGroup];
            },
            hasOtherUsersTemplates() {
                return !!this.otherUsersGroup.templates.length;
            },
            isOtherUsersTemplate() {
                return this.currentTemplateGroup === permissionSources.otherUsers;
            },
            isRoleTemplate() {
                return this.currentTemplateGroup === permissionSources.roles;
            },
            isCustomizedTemplate() {
                return this.currentTemplateGroup === permissionSources.customized;
            },
            isPermissionGridVisible() {
                return this.isCustomizedTemplate
                    || (this.isOtherUsersTemplate
                    && this.otherUsersGroup.selected)
                    || (this.isRoleTemplate
                    && this.rolesGroup.selected);
            },
            allPermissions() {
                const resourceNames = Object.keys(this.resources);
                return resourceNames.map(resourceName => (
                    {
                        resourceName,
                        resourceLabel: this.resources[resourceName],
                        actions: this.value[resourceName] || [],
                    }));
            },
        },
        validations: {},
        methods: {
            getDefaultTemplateGroup() {
                if (getMatchingRole(this.value)) {
                    return permissionSources.roles;
                }
                if (!_.isEmpty(this.value)) {
                    return permissionSources.customized;
                }
                if (this.hasOtherUsersTemplates) {
                    return permissionSources.otherUsers;
                }
                return permissionSources.roles;
            },
            getSelectedRole() {
                return getMatchingRole(this.value);
            },
            templateSelected({value, permissions}, templateGroup) {
                templateGroup.selected = value;
                this.updatePermissions(permissions);
            },
            updateLimitedAccess(isLimitedAccess) {
                this.isLimitedAccess = isLimitedAccess;
                if (!this.isLimitedAccess) {
                    this.updatePermissions(null);
                } else {
                    this.updatePermissions({});
                    this.currentTemplateGroup = this.getDefaultTemplateGroup();
                    this.currentGroup.selected = null;
                }
            },
            updateTemplateGroup(templateGroup) {
                this.currentTemplateGroup = templateGroup;
                if (this.isCustomizedTemplate) {
                    this.updatePermissions(this.customizedPermissions);
                } else {
                    this.currentGroup.selected = null;
                }
            },
            updatePermissions(permissions) {
                this.$emit('input', permissions);
            },
            updateAction(resourceName, actions) {
                const newPermissions = {
                    ...this.value,
                    [resourceName]: actions.includes(ACTIONS.all) ? [ACTIONS.all] : actions,
                };
                if (!actions || !actions.length) {
                    delete newPermissions[resourceName];
                }
                this.updatePermissions(newPermissions);
            },
            /**
             * Fetch other users permissions
             * Only if is another user
             * Only if the user fetched is not an admin
             * */
            async fetchData() {
                const {items} = await this.$api(({users}) => users.getAll());
                const userTemplates = items.filter(({fields: {id, permissions}}) => id !== this.userId && !isAdmin(permissions));
                if (!userTemplates.length) { return; }
                this.otherUsersGroup.templates = userTemplates.map(({fields}) => (
                    {
                        value: fields.id,
                        label: `${fields.firstName} ${fields.lastName}`,
                        permissions: methodToActionPermissions(fields.permissions),
                    }));
            },
            clearAllSelected() {
                this.updatePermissions({});
            },
        },
    };
</script>
